import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import { batchGeo } from "api/layer";
import countryArray from "assets/json/country.json";
import { AppAction, AppContext } from "helpers/context";
import { getLanguage } from "helpers/translations";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AddressEncodingContent from "./address-encoding-content/address-encoding-content";
import AddressEncodingHeader from "./address-encoding-header/address-encoding-header";
import AddressEncodingLoading from "./address-encoding-loading/address-encoding-loading";
import { pollingFunction } from "./address-encoding-polling";

function AddressEncoding() {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();
  const [loadingText, setLoadingText] = useState("");

  const [data, setData] = useState([] as any[]);
  const [candidatesData, setCandidatesData] = useState([] as any[]);
  const [noMatchData, setNoMatchData] = useState([] as any[]);
  const [matchData, setMatchData] = useState([] as any[]);

  const language = getLanguage();
  const importMultipleLocations = context.importMultipleLocations;

  const init = async () => {
    const locationData = importMultipleLocations?.items?.map((item) => {
      const { id, number, place, street, province, country } = item;

      let address = item?.address;
      if (!address) {
        const addressEn = `${number || ""} ${street || ""} ${place || ""} ${
          province || ""
        } ${country || ""}`;
        const addressCn = `${country || ""} ${province || ""}  ${place || ""} ${
          street || ""
        } ${number || ""}`;

        address = (language === "en" ? addressEn : addressCn).replace(
          "undefined",
          ""
        );
      }

      const countryObj = countryArray.find((e) => e.label === country);

      return {
        locationId: id,
        address,
        language,
        country: countryObj?.value
      };
    });

    if (!locationData || locationData.length === 0) {
      return;
    }

    setLoadingText(t("addressEncoding.progress.start"));

    try {
      const res: any = await batchGeo({
        addressList: locationData
      });

      if (res?.code !== 200) {
        setLoadingText("");
        return;
      }

      const batchId = res?.data?.batchId;

      if (!batchId) {
        setLoadingText("");
        return;
      }

      setLoadingText(t("addressEncoding.progress.queue"));

      const pollCount = locationData.length / 100;

      const batchGeoResults = await pollingFunction(
        3000,
        batchId,
        dispatch,
        pollCount + 3
      );

      if (batchGeoResults?.length) {
        setData(batchGeoResults);

        dispatch({
          type: AppAction.setAddressEncodingData,
          payload: batchGeoResults
        });
      }
    } catch (error) {
      console.error(
        "An error occurred during address encoding process:",
        error
      );
    } finally {
      setLoadingText("");
    }
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="stretch"
      sx={{ p: 2, height: "100vh", backgroundColor: "#f0f0f0" }}
    >
      <AddressEncodingHeader
        matchData={matchData}
        candidatesData={candidatesData}
        noMatchData={noMatchData}
      />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {loadingText ? (
          <AddressEncodingLoading text={loadingText} />
        ) : (
          <AddressEncodingContent
            data={data}
            candidatesData={candidatesData}
            setCandidatesData={setCandidatesData}
            noMatchData={noMatchData}
            setNoMatchData={setNoMatchData}
            matchData={matchData}
            setMatchData={setMatchData}
          />
        )}
      </Box>
    </Grid>
  );
}

export default AddressEncoding;
